import * as React from "react"
import { Draggable } from "react-beautiful-dnd"

import { shallowEqual } from "shared/utils/javascript"

import { ItemRenderFunction } from "../types"

interface ItemProps<ItemType> {
  item: ItemType
  index: number
  className?: string
  selectItem: (item: ItemType) => void
  isDraggingOver: boolean
  isSelected: boolean
  renderItem: ItemRenderFunction<ItemType>
}

const Item = <ItemType extends { id: number }>({
  item,
  index,
  className,
  selectItem,
  isDraggingOver,
  isSelected,
  renderItem,
}: ItemProps<ItemType>): JSX.Element => {
  return (
    <Draggable draggableId={String(item.id)} index={index}>
      {(provided, snapshot) => {
        const isBeingDragged = snapshot.isDragging && !snapshot.isDropAnimating

        const element = renderItem(item, {
          ...{ isSelected, isBeingDragged, isDraggingOver },
          dragHandleProps: provided.dragHandleProps,
        })

        return (
          <div
            ref={provided.innerRef}
            data-testid="navigator-item"
            data-item-id={item.id}
            {...provided.draggableProps}
            onClick={() => selectItem(item)}
            className={className}
          >
            {element}
          </div>
        )
      }}
    </Draggable>
  )
}

// shallowEqual is React.memo's default comparison algorithm and the second parameter of React.memo could therefore be omitted.
// We only keep it as a breakpoint placeholder to help troubleshooting perf issues.
const MemoisedItem = React.memo(Item, (prevProps, nextProps) => shallowEqual(prevProps, nextProps))

// "as typeof" is required to export the constant as a parameterised generic
export default MemoisedItem as typeof Item
